import React, { useState, useEffect } from "react";
import adminClasses from "../../../landing-pages/admin/admin.module.css";
import IdNameModel from "../../../../models/common/IdNameModel";
import modalUtils from "../../../../utils/modal-utils";
import loaderUtils from "../../../../utils/loader-utils";
import toasterUtils from "../../../../utils/toaster-utils";
import dateTimeUtils from "../../../../utils/date-utils";
import { PagingEnum } from "../../../../models/enums/IAIEnum";
import masterDataService from "../../../../services/master-data-service";
import RequirementEscalationsSearchInputModel from "../../../../models/company/escalation/request/RequirementEscalationsSearchInputModel";
import PagedListModel from "../../../../models/common/PagedListModel";
import RequirementEscalationsGridModel from "../../../../models/company/escalation/response/RequirementEscalationsGridModel";
import clientEscalationService from "../../../../services/company/client-escalation-service";
import { SortDirection } from "../../../../models/enums/SortDirectionEnum";
import RequirementEscalationsRequestModel from "../../../../models/company/escalation/request/RequirementEscalationsRequestModel";
import EscalationsGridComponent from "./EscalationsGridComponent";
import { GeneralPoupConfig, ModalSize } from "../../../../models/modal-types";
import EscalationsUpdateComponent from "./EscalationsUpdateComponent";
import EscalationHistoryComponent from "./EscalationHistoryComponent";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GuidEnum } from "../../../../models/enums/GuidEnum";
import adminCompanyService from "../../../../services/admin/admin-company-service";

interface EscalationsProps {
    statusList: IdNameModel[];
    companiesList: IdNameModel[];
};

const EscalationsComponent = (props: EscalationsProps) => {
    const [escalationsPagedList, setEscalationsPagedList] = useState<PagedListModel<RequirementEscalationsGridModel>>();
    const [escalationsList, setEscalationsList] = useState<RequirementEscalationsGridModel[]>([]);
    const [jobcodeList, setJobcodeList] = useState<IdNameModel[]>([]);
    const [refreshEscalations, setRefreshEscalations] = useState<number>(2);
    const [startDateSearch, setStartDateSearch] = useState<Dayjs | null>(null);
    const [endDateSearch, setEndDateSearch] = useState<Dayjs | null>(null);
    const [companySearch, setCompanySearch] = useState<string>(GuidEnum.Empty);
    const [jobcodeSearch, setJobcodeSearch] = useState<string>(GuidEnum.Empty);
    const [jobcodeFilter, setJobcodeFilter] = useState<string>("");
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("EscalationDate");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirection);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);

    useEffect(() => {
        const today = dayjs();
        setStartDateSearch(today.startOf("month"));
        setEndDateSearch(today.endOf("month"));
        loadPageSizes();
        getEscalations(getSearchFilters(jobcodeFilter, page, pageSize, sortColumn, sortDirection));
    }, []);

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (jobcodeFilter: string, page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        const today = dayjs();
        var inputData: RequirementEscalationsSearchInputModel = {
            startDate: dateTimeUtils.getFormattedDate(startDateSearch ? startDateSearch.toDate() : today.startOf("month").toDate()),
            endDate: dateTimeUtils.getFormattedDate(endDateSearch ? endDateSearch.toDate() : today.endOf("month").toDate()),
            jobCode: jobcodeFilter,
            escalatedBy: GuidEnum.Empty,
            requirementId: jobcodeSearch,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getEscalations = (inputData: RequirementEscalationsSearchInputModel) => {
        setRefreshEscalations(2);
        modalUtils.showLoader();
        clientEscalationService.getEscalations(inputData).then((res) => {
            modalUtils.closeModal();
            setEscalationsList([]);
            setEscalationsPagedList(res);
            setEscalationsList(res.items);
            setRefreshEscalations(refreshEscalations * 5);
        }).catch((error) => {
            setRefreshEscalations(refreshEscalations * 5);
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const loadJobcodes = (companyId: string) => {
        setCompanySearch(companyId);
        setJobcodeSearch(GuidEnum.Empty);
        modalUtils.showLoader();
        adminCompanyService.getJobCodesForCompany(companyId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setJobcodeList(res.items);
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onEscalationAddActionHandler = (objEscalation: RequirementEscalationsRequestModel) => {
        const config: GeneralPoupConfig = {
            title: "Add Escalation",
            content: (
                <EscalationsUpdateComponent
                    objEscalation={objEscalation}
                    statusList={props.statusList}
                    onEscalationSaveAction={onEscalationSaveActionHandler}
                    onEscalationCloseAction={onEscalationCloseActionHandler} />
            ),
            size: ModalSize.LG,
            // className: "model-min-height-500"
        };
        modalUtils.showPopup(config);
    };

    const onEscalationCloseActionHandler = () => {
        modalUtils.closeModal();
    };

    const onEscalationSaveActionHandler = (objEscalation: RequirementEscalationsRequestModel) => {
        modalUtils.showLoader();
        clientEscalationService.saveEscalation(objEscalation).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            getEscalations(getSearchFilters(jobcodeFilter, page, pageSize, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
        getEscalations(getSearchFilters(jobcodeFilter, newPage, pageSize, sortColumn, sortDirection));
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
        getEscalations(getSearchFilters(jobcodeFilter, PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    const onSortActionHandler = (newSortColumn: string, sortDir: string) => {
        setSortColumn(newSortColumn);
        setSortDirection(sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc);
        getEscalations(getSearchFilters(jobcodeFilter, page, pageSize, newSortColumn, sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc));
    };

    const onFilterActionHandler = (filterColumn: string, filterValue: string) => {
        setPage(0);
        setJobcodeFilter(filterValue);
        getEscalations(getSearchFilters(filterValue, 0, pageSize, sortColumn, sortDirection));
    };

    const onShowHistoryActionHandler = (escalationId: string) => {
        modalUtils.showLoader();
        clientEscalationService.getEscalationHistory(escalationId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            const config: GeneralPoupConfig = {
                title: "History",
                content: (
                    <EscalationHistoryComponent
                        escalationsList={res.items} />
                ),
                size: ModalSize.LG,
                className: "model-min-height-500"
            };
            modalUtils.showPopup(config);
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    return (
        <div>
            <div className="card card-white">
                <div className="card-head">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="apply-filter">
                                Filter Escalations <i className="fas fa-filter" data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight" aria-controls="offcanvasRight"></i>
                            </div>
                            <div className="offcanvas offcanvas-end" id="offcanvasRight" aria-labelledby="offcanvasRightLabel">
                                <div className="offcanvas-header">
                                    <h5 className="offcanvas-title" id="offcanvasRightLabel">Apply Filter</h5>
                                    <a type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></a>
                                </div>
                                <div className="offcanvas-body">
                                    <div className="row">
                                        <div>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Start Date"
                                                    value={startDateSearch}
                                                    onChange={(newValue) => setStartDateSearch(newValue)}
                                                    format="YYYY-MMM-DD"
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            size: "small",
                                                            margin: "normal",
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                        <div>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="End Date"
                                                    value={endDateSearch}
                                                    onChange={(newValue) => setEndDateSearch(newValue)}
                                                    format="YYYY-MMM-DD"
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            size: "small",
                                                            margin: "normal",
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                        <div>
                                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                                <Autocomplete options={props.companiesList} value={props.companiesList.find((x) => x.guId === companySearch) || null}
                                                    onChange={(_, newValue) => loadJobcodes(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Company" size="small" margin="normal" fullWidth />
                                                    )}
                                                />
                                            </FormControl>
                                        </div>
                                        <div>
                                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                                <Autocomplete options={jobcodeList} value={jobcodeList.find((x) => x.guId === jobcodeSearch) || null}
                                                    onChange={(_, newValue) => setJobcodeSearch(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Jobcodes" size="small" margin="normal" fullWidth />
                                                    )}
                                                />
                                            </FormControl>
                                        </div>
                                        <div className="text-center">
                                            <Button variant="contained" color="success" onClick={() => getEscalations(getSearchFilters(jobcodeFilter, page, pageSize, sortColumn, sortDirection))} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                                                Search
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {refreshEscalations > 2 &&
                        <EscalationsGridComponent
                            key={refreshEscalations}
                            escalationsList={escalationsList}
                            totalSize={escalationsPagedList?.totalCount ? escalationsPagedList?.totalCount : escalationsList.length}
                            currentPage={page + 1}
                            pagesize={pageSize}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                            pageSizesList={pageSizesList}
                            jobcodeFilterValue={jobcodeFilter}
                            disablePagingAndSorting={false}
                            onEscalationAddAction={onEscalationAddActionHandler}
                            onPageChangeAction={onPageChangeActionHandler}
                            onPageSizeChangeAction={onPageSizeChangeActionHandler}
                            onSortAction={onSortActionHandler}
                            onFilterAction={onFilterActionHandler}
                            onShowHistoryAction={onShowHistoryActionHandler} />
                    }
                </div>
            </div>
        </div>
    );
};
export default EscalationsComponent;